// File: fbMdbAuthBase-100824/client/src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { auth } from '../../services/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_EXTENDED_NEURO_URL; //'http://localhost:4000/api/users';

const initialState = {
  user: null,
  isLoading: false,
  error: null,
};

export const register = createAsyncThunk(
  'auth/register',
  async ({ email, password, displayName }, thunkAPI) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();
      
      await axios.post(`${API_URL}/users/register`, {
        firebaseUid: user.uid,
        email,
        displayName,
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      return { uid: user.uid, email: user.email, displayName };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, thunkAPI) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();
      
      const response = await axios.post(`${API_URL}/users/login`, { email, password }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (response.data && response.data.user) {
        return {
          ...response.data.user,
          token: response.data.token
        };
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, thunkAPI) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        await axios.post(`${API_URL}/users/logout`, {}, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
      }
      await signOut(auth);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, thunkAPI) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('No user logged in, cannot refresh token');
        return thunkAPI.rejectWithValue('No user logged in');
      }
      const newToken = await user.getIdToken(true);
      console.log('Token refreshed successfully');
      return { token: newToken };
    } catch (error) {
      console.error('Error refreshing token:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchUserData = createAsyncThunk(
  'auth/fetchUserData',
  async (_, thunkAPI) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('No user logged in, cannot fetch user data');
        return thunkAPI.rejectWithValue('No user logged in');
      }
      const idToken = await user.getIdToken();
      const response = await axios.get(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      console.log('User data fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.user = null;
    },
    updateUserData: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = {
          uid: action.payload.uid,
          email: action.payload.email,
          displayName: action.payload.displayName,
          // Add any other relevant user data here
        };
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('Login fulfilled payload:', action.payload);
        state.user = {
          uid: action.payload.uid,
          email: action.payload.email,
          displayName: action.payload.displayName,
          role: action.payload.role,
          token: action.payload.token,
          ...action.payload
        };
        console.log('Updated user state:', state.user);
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
      })
      .addCase(fetchUserData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        state.isLoading = false;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (state.user) {
          state.user.token = action.payload.token;
        }
      });
  },
});

export const { clearUser, updateUserData } = authSlice.actions;
export default authSlice.reducer;

// Selector to get user data
export const selectUser = (state) => state.auth.user;
